<template>
  <AnnotatorHost18170
    ref="rootElementRef"
    v-bind="$attrs"
    oncopy="return false"
    :content="content"
    @highlightsRendered="emit('highlightsRendered')"
    @loaded="replaceLinks"
  >
    <div
      class="sf-chapter"
      @click="onBodyContentClick"
      v-html="chapterHtml"
    ></div>
  </AnnotatorHost18170>
  <!--
    Continue Reading Foundations (#19038), variant B:
    the chapter is completed only after bottom of the chapter has been on the screen.
  -->
  <div v-on-visible.once="onChapterBottomSeen" class="sf-chapter__bottom"></div>
  <EOCButton
    v-if="isChapterEOC()"
    text="Start Free Trial to Unlock"
    ios-text="Full Subscription Required to Read More"
  />
</template>

<script setup lang="ts">
import {
  type ComponentPublicInstance,
  createApp,
  h,
  onBeforeUnmount,
  onMounted,
  ref,
} from 'vue'

import AnnotatorHost18170 from '@/app/components/doc/reading/AnnotatorHost18170.vue'
import EOCButton from '@/app/components/billing/EOCButton.vue'
import AdditionalInformation from '@/app/components/doc/reading/AdditionalInformation.vue'

import type { ContentView } from '@/models/doc'
import { replaceLinksForAndroid } from '@/helpers/url.android'
import { parseTextForTranslation } from '@/services/weglot'
import { Mixpanel } from '@/analytics/mixpanel'
import { Capacitor } from '@capacitor/core'
import { useInternalLinkReplacement } from '@/app/composables/useInternalLinkReplacement'
import { vOnVisible } from '@/helpers/directives'

const props = defineProps<{
  content: ContentView
}>()

const emit = defineEmits<{
  (event: 'highlightsRendered'): void
}>()

const rootElementRef = ref<ComponentPublicInstance | null>(null)
const { replaceLinks, cleanup } = useInternalLinkReplacement(rootElementRef)
const chapterHtml = ref('')

function getHtml(): string {
  return `<h2>${props.content.title}</h2>
      ${parseTextForTranslation(removeLinksForAndroid(props.content.text))}
      ${getAdditionalMaterialsHtml()}`
}

onMounted(() => {
  chapterHtml.value = getHtml()
})

onBeforeUnmount(async () => {
  cleanup()
})

// Remove blacklisted links while preserving inner text due to Google Payment policy (#16556, #16717)
function removeLinksForAndroid(text: string): string {
  if (Capacitor.getPlatform() === 'android') {
    text = replaceLinksForAndroid(text)
  }
  return text
}

function isChapterEOC(): boolean {
  return props.content.isChapterEOC
}

/**
 * Returns the HTML for the additional materials section.
 *
 * We want annotator work on the additional materials section,
 * and, at the same time, we need to have one root element for the
 * annotator (it is `.highlights-content > div.sf-chapter`, see
 * @/app/components/doc/reading/Annotator.vue).
 *
 * So, we create a temporary Vue app, mount it to a div, and then
 * return the outerHTML of the mounted app's root element.
 * That HTML is then inserted into the div with chapter HTML.
 *
 * See also: #11402.
 */
function getAdditionalMaterialsHtml(): string {
  const commentaries = props.content.commentaries

  if (!commentaries || props.content.isChapterEOC) {
    return ''
  }

  // eslint-disable-next-line vue/one-component-per-file
  const tempApp = createApp({
    render() {
      return h(AdditionalInformation, {
        commentaries: commentaries,
      })
    },
  })

  const el = document.createElement('div')
  const mountedApp = tempApp.mount(el)
  const mountedAppHtml = mountedApp.$el.outerHTML

  // Remove the temporary app from the DOM.
  el.remove()
  tempApp.unmount()

  return `
      <div class="sf-chapter__additional-information">
        ${mountedAppHtml}
      </div>
    `
}

/**
 * Catch clicks on <a> for "Doc Link Click" event.
 */
function onBodyContentClick(event: any): void {
  if (event?.target.tagName === 'A') {
    const href = event.target.getAttribute('href')
    if (href) {
      Mixpanel.trackDocLinkClick(href, props.content.doc)
    }
  }
}

/**
 * Report that the reader has reached the bottom of the chapter.
 */
function onChapterBottomSeen(): void {
  props.content.doc.readingProgress?.markBottomSeen()
}
</script>

<style lang="scss" scoped>
.sf-chapter__bottom {
  // The directive needs 25% of the element to be visible, and the ratio of a
  // zero-height element is not worth relying on.
  height: 1px;
}
</style>
